import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Loader2, X, Save } from "lucide-react";
import { useAuth } from "../lib/auth";

const TABS = ["Categories", "Sizes", "Colours", "Lot Numbers", "Racks",
              "Factories", "Warehouses", "Billing", "Min Stock",
              "Custom Fields", "PO Layout", "Daily Summary", "Users",
              "Photo Cleanup", "Security"];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Categories");
  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Configuration</div>
        <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">Settings.</h1>
      </header>
      <div className="border-b border-[#E5D8C4] flex gap-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            data-testid={`settings-tab-${t.toLowerCase().replace(/\s+/g,'-')}`}
            className={`pb-3 -mb-px text-sm font-medium border-b-2 whitespace-nowrap ${
              tab===t?"border-[#2D2622] text-[#2D2622]":"border-transparent text-[#8B6F47] hover:text-[#2D2622]"}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Categories" && <Categories />}
      {tab === "Sizes" && <Sizes />}
      {tab === "Colours" && <Colours />}
      {tab === "Lot Numbers" && <NamedList collection="lot_numbers" label="Lot Number" />}
      {tab === "Racks" && <RacksAdmin />}
      {tab === "Factories" && <NamedList collection="factories" label="Factory" hasNote />}
      {tab === "Warehouses" && <NamedList collection="warehouses" label="Warehouse Place" hasNote />}
      {tab === "Billing" && <NamedList collection="billing_companies" label="Billing Company" />}
      {tab === "Min Stock" && <MinStock />}
      {tab === "Custom Fields" && (user?.role === "admin" ? <CustomFields /> : <div className="text-sm text-[#8B6F47]">Admin only.</div>)}
      {tab === "PO Layout" && (user?.role === "admin" ? <POLayout /> : <div className="text-sm text-[#8B6F47]">Admin only.</div>)}
      {tab === "Daily Summary" && <DailySummary />}
      {tab === "Users" && (user?.role === "admin" ? <Users /> : <div className="text-sm text-[#8B6F47]">Admin only.</div>)}
      {tab === "Photo Cleanup" && (user?.role === "admin" ? (
        <div className="space-y-5">
          <PhotoCleanup />
          <MetadataScrub />
        </div>
      ) : <div className="text-sm text-[#8B6F47]">Admin only.</div>)}
      {tab === "Security" && (user?.role === "admin" ? <Security /> : <div className="text-sm text-[#8B6F47]">Admin only.</div>)}
    </div>
  );
}

/* ---------------- Categories ---------------- */
function Categories() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name:"", min_photos:1, photo_labels_str:"", barcode_on_label: true });
  const load = () => api.get("/categories").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", {
        name: f.name, min_photos: parseInt(f.min_photos,10),
        photo_labels: f.photo_labels_str.split(",").map(s=>s.trim()).filter(Boolean),
        barcode_on_label: f.barcode_on_label,
      });
      setF({ name:"", min_photos:1, photo_labels_str:"", barcode_on_label: true });
      toast.success("Category added"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleBarcode = async (c) => {
    try {
      await api.patch(`/categories/${c.id}`, {
        name: c.name, min_photos: c.min_photos, photo_labels: c.photo_labels || [],
        barcode_on_label: !c.barcode_on_label,
      });
      toast.success(`Barcode ${!c.barcode_on_label ? "enabled" : "disabled"} for ${c.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="bg-white border border-[#E5D8C4] p-5 space-y-3 h-fit">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">New category</div>
        <input required data-testid="cat-name" placeholder="Name (e.g. Marble)" value={f.name}
          onChange={(e)=>setF({...f, name:e.target.value})}
          className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        <input required data-testid="cat-min" type="number" min="1" placeholder="Min photos" value={f.min_photos}
          onChange={(e)=>setF({...f, min_photos:e.target.value})}
          className="w-full h-10 px-3 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
        <input data-testid="cat-labels" placeholder="Photo labels (comma separated)"
          value={f.photo_labels_str} onChange={(e)=>setF({...f, photo_labels_str:e.target.value})}
          className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={f.barcode_on_label}
            onChange={(e)=>setF({...f, barcode_on_label:e.target.checked})} />
          Print barcode on label (uncheck for panel-only categories like 3D)
        </label>
        <button data-testid="cat-add" className="w-full h-10 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
          <Plus size={14}/> Add
        </button>
      </form>
      <div className="lg:col-span-2 bg-white border border-[#E5D8C4] divide-y divide-[#F3ECDF]">
        {items.map(c => (
          <div key={c.id} className="p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display font-semibold text-[#2D2622]">{c.name}</div>
              <div className="text-[11px] text-[#8B6F47] font-mono2 mt-0.5">
                Min photos: {c.min_photos} · {(c.photo_labels||[]).join(" · ")}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8B6F47] cursor-pointer">
                <input type="checkbox" checked={c.barcode_on_label !== false}
                  data-testid={`cat-bc-${c.name.toLowerCase().replace(/\s+/g,'-')}`}
                  onChange={()=>toggleBarcode(c)} />
                Barcode
              </label>
              <button onClick={()=>remove(c.id)} className="p-2 text-[#8B6F47] hover:text-red-600"><Trash2 size={15}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Min Stock rules ---------------- */
function MinStock() {
  const [rules, setRules] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [cats, setCats] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [f, setF] = useState({ category_id:"", color_id:"", size_id:"", min_qty:5 });
  const load = () => Promise.all([
    api.get("/min-stock-rules").then(r => setRules(r.data)),
    api.get("/reports/low-stock").then(r => setLowStock(r.data)),
    api.get("/categories").then(r => setCats(r.data)),
  ]);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!f.category_id) { setColors([]); setSizes([]); return; }
    api.get("/colors", { params: { category_id: f.category_id } }).then(r => setColors(r.data));
    api.get("/sizes", { params: { category_id: f.category_id } }).then(r => setSizes(r.data));
  }, [f.category_id]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/min-stock-rules", {
        category_id: f.category_id,
        color_id: f.color_id || null, size_id: f.size_id || null,
        min_qty: parseInt(f.min_qty, 10),
      });
      setF({ category_id:"", color_id:"", size_id:"", min_qty:5 });
      toast.success("Rule added"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    try { await api.delete(`/min-stock-rules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const catName = (id) => cats.find(c => c.id === id)?.name || "—";
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="bg-white border border-[#E5D8C4] p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Add min-stock rule</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select required value={f.category_id} data-testid="min-cat"
            onChange={(e)=>setF({...f, category_id:e.target.value, color_id:"", size_id:""})}
            className="h-10 px-3 border border-[#E5D8C4] text-sm bg-white">
            <option value="">Category…</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={f.color_id} onChange={(e)=>setF({...f, color_id:e.target.value})}
            className="h-10 px-3 border border-[#E5D8C4] text-sm bg-white" disabled={!f.category_id}>
            <option value="">Any colour</option>
            {colors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={f.size_id} onChange={(e)=>setF({...f, size_id:e.target.value})}
            className="h-10 px-3 border border-[#E5D8C4] text-sm bg-white" disabled={!f.category_id}>
            <option value="">Any size</option>
            {sizes.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <input required type="number" min="1" data-testid="min-qty" placeholder="Min qty" value={f.min_qty}
              onChange={(e)=>setF({...f, min_qty:e.target.value})}
              className="flex-1 h-10 px-3 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
            <button data-testid="min-add" className="h-10 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em]">Add</button>
          </div>
        </div>
      </form>
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Current rules</div>
        <div className="bg-white border border-[#E5D8C4] divide-y divide-[#F3ECDF]">
          {rules.length === 0 && <div className="p-4 text-sm text-[#8B6F47]">No rules yet.</div>}
          {rules.map(r => (
            <div key={r.id} className="p-3 flex justify-between items-center gap-3">
              <div className="text-sm text-[#2D2622]">
                <b>{catName(r.category_id)}</b>{r.color_id && ` · colour`}{r.size_id && ` · size`}
                <span className="ml-2 font-mono2 text-[#8B6F47]">min {r.min_qty} pcs</span>
              </div>
              <button onClick={()=>remove(r.id)} className="p-1.5 text-[#8B6F47] hover:text-red-600"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#C2410C] font-semibold mb-3">Currently below minimum</div>
        <div className="bg-white border border-[#E5D8C4]">
          {lowStock.length === 0 ? (
            <div className="p-4 text-sm text-[#8B6F47]">Everything above threshold. ✓</div>
          ) : (
            <div className="divide-y divide-[#F3ECDF]">
              {lowStock.map((l) => (
                <div key={`${l.category_id}-${l.color_id}-${l.size_id}`} className="p-3 flex justify-between items-center gap-3">
                  <div className="text-sm text-[#2D2622]">
                    {l.category_name} · {l.color_name} · {l.size_label}
                  </div>
                  <div className="font-mono2 text-sm">
                    <span className="text-[#C2410C] font-bold">{l.on_hand}</span>
                    <span className="text-[#8B6F47]"> / {l.min_qty}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Daily Summary ---------------- */
function DailySummary() {
  const [data, setData] = useState(null);
  const today = new Date().toISOString().slice(0,10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [dispatchOnly, setDispatchOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/daily-summary", {
        params: { date_from: dateFrom, date_to: dateTo, dispatch_only: dispatchOnly }
      });
      setData(r.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dateFrom, dateTo, dispatchOnly]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.text);
      setCopied(true); toast.success("Copied — paste into WhatsApp");
      setTimeout(() => setCopied(false), 3000);
    } catch { toast.error("Clipboard access denied"); }
  };
  const whatsappUrl = data ? `https://wa.me/?text=${encodeURIComponent(data.text)}` : "#";
  const downloadCsv = (kind) => {
    // Auth carrier is the HTTP-only cookie sent automatically by axios
    // (`withCredentials: true` in api.js). No token needs to be attached
    // to the URL — the previous `?auth=` fallback URL was dead code.
    api.get(`/reports/export.csv?kind=${kind}`, { responseType: "blob" }).then(r => {
      const blob = new Blob([r.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `veneer-slate-${kind}.csv`;
      link.click();
    }).catch(() => toast.error("Export failed"));
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-1">From</div>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              data-testid="daily-from"
              className="h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
          </div>
          <div>
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-1">To</div>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              data-testid="daily-to"
              className="h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
          </div>
          <label className="flex items-center gap-2 text-xs h-10 px-3 border border-[#E5D8C4] cursor-pointer">
            <input type="checkbox" checked={dispatchOnly}
              data-testid="daily-dispatch-only"
              onChange={(e) => setDispatchOnly(e.target.checked)} />
            Dispatch only (for WhatsApp)
          </label>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={copy} disabled={!data || busy} data-testid="daily-copy"
            className="h-10 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40">
            {copied ? "✓ Copied" : "Copy for WhatsApp"}
          </button>
          <a href={whatsappUrl} target="_blank" rel="noreferrer"
            className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-[0.2em] inline-flex items-center hover:border-[#2D2622]">
            Open WhatsApp
          </a>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <KPI label="Stock In" value={data.counts.in} accent="text-green-700"/>
            <KPI label="Stock Out" value={data.counts.out} accent="text-orange-700"/>
            <KPI label="PO Dispatched" value={data.counts.dispatched_pos} accent="text-[#2D2622]"/>
          </div>
          <div className="bg-white border border-[#E5D8C4] p-4">
            <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">
              WhatsApp preview {dispatchOnly ? "(dispatch-only)" : "(full)"}
            </div>
            <pre className="whitespace-pre-wrap font-mono2 text-xs text-[#2D2622] leading-relaxed">{data.text}</pre>
          </div>
        </>
      )}
      <div className="bg-white border border-[#E5D8C4] p-4">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Excel / CSV exports</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadCsv("lots")} data-testid="csv-lots"
            className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622]">Lots CSV</button>
          <button onClick={() => downloadCsv("movements")} data-testid="csv-mov"
            className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622]">Movements CSV</button>
          <button onClick={() => downloadCsv("purchase_orders")} data-testid="csv-po"
            className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622]">Purchase Orders CSV</button>
        </div>
      </div>
      <WhatsAppAutomation />
    </div>
  );
}

/* ---------------- WhatsApp Automation ---------------- */
function WhatsAppAutomation() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/whatsapp/config"),
        api.get("/whatsapp/logs"),
      ]);
      setCfg(c.data); setLogs(l.data);
    } catch (err) {
      // Non-admins get 403; expected for operators without whatsapp.view perm.
      console.debug("whatsapp config load skipped:", err.response?.status);
    }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return null;
  if (!cfg) return null;

  const save = async (patch) => {
    setBusy(true);
    try {
      // strip masked secrets (contain •) unless user typed new value
      const payload = { ...cfg, ...patch };
      Object.keys(payload).forEach(k => {
        if (typeof payload[k] === "string" && payload[k].includes("•")) delete payload[k];
      });
      const r = await api.put("/whatsapp/config", payload);
      setCfg(r.data); toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const addPhone = () => {
    const p = phoneInput.trim().replace(/[+\s()-]/g, "");
    if (!p) return;
    if ((cfg.admin_phones || []).includes(p)) { toast.error("Already added"); return; }
    save({ admin_phones: [...(cfg.admin_phones || []), p] });
    setPhoneInput("");
  };
  const removePhone = (p) => save({ admin_phones: (cfg.admin_phones || []).filter(x => x !== p) });
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/whatsapp/send-now");
      toast.success(`Attempted ${r.data.sent} sends`);
      const l = await api.get("/whatsapp/logs"); setLogs(l.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-[#E5D8C4] p-4 space-y-4" data-testid="whatsapp-automation">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">WhatsApp automation</div>
          <div className="text-xs text-[#5D5148] mt-1">
            Auto-sends the daily summary at <strong>9:00 PM IST</strong> to admin numbers below. Covers yesterday + today.
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="wa-enabled"
            onChange={(e) => save({ enabled: e.target.checked })} />
          Enable auto-send
        </label>
      </div>

      {/* Admin phones */}
      <div>
        <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Admin phone numbers (E.164, digits only, e.g. 919876543210)</div>
        <div className="flex gap-2 flex-wrap mb-2">
          {(cfg.admin_phones || []).map(p => (
            <span key={p} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F3ECDF] text-xs font-mono2 text-[#2D2622]" data-testid={`wa-phone-${p}`}>
              +{p}
              <button onClick={() => removePhone(p)} className="text-[#8B6F47] hover:text-red-600" data-testid={`wa-phone-remove-${p}`}>
                <X size={12}/>
              </button>
            </span>
          ))}
          {(cfg.admin_phones || []).length === 0 && (
            <span className="text-xs text-[#8B6F47]">No admin numbers yet.</span>
          )}
        </div>
        <div className="flex gap-2">
          <input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)}
            data-testid="wa-phone-input"
            placeholder="e.g. 919876543210"
            className="flex-1 h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] font-mono2" />
          <button onClick={addPhone} disabled={busy} data-testid="wa-phone-add"
            className="h-10 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em]">Add</button>
        </div>
      </div>

      {/* Gallabox credentials */}
      <div>
        <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Gallabox credentials (optional — leave blank to send manually via wa.me links)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Account ID" value={cfg.gallabox_account_id}
            onSave={(v) => save({ gallabox_account_id: v })} testId="wa-account-id"/>
          <Field label="Channel ID" value={cfg.gallabox_channel_id}
            onSave={(v) => save({ gallabox_channel_id: v })} testId="wa-channel-id"/>
          <Field label="Template Name" value={cfg.gallabox_template_name}
            onSave={(v) => save({ gallabox_template_name: v })} testId="wa-template-name"/>
          <Field label="Template Language" value={cfg.gallabox_template_language}
            onSave={(v) => save({ gallabox_template_language: v })} testId="wa-template-lang"/>
          <Field label="API Key" value={cfg.gallabox_api_key}
            onSave={(v) => save({ gallabox_api_key: v })} testId="wa-api-key" secret/>
          <Field label="API Secret" value={cfg.gallabox_api_secret}
            onSave={(v) => save({ gallabox_api_secret: v })} testId="wa-api-secret" secret/>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button onClick={sendNow} disabled={sending || !(cfg.admin_phones || []).length}
          data-testid="wa-send-now"
          className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40 inline-flex items-center gap-2">
          {sending ? <Loader2 className="animate-spin" size={14}/> : null}
          Send now (test)
        </button>
      </div>

      {/* Delivery log */}
      {logs.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">Delivery log (last 50)</div>
          <div className="border border-[#E5D8C4] max-h-64 overflow-y-auto divide-y divide-[#F3ECDF]" data-testid="wa-logs">
            {logs.map((l) => (
              <div key={l.id} className="p-2 text-xs flex flex-wrap gap-3 items-center">
                <span className="font-mono2 text-[#8B6F47]">{l.sent_at?.slice(0,19).replace("T", " ")}</span>
                <span className="font-mono2 text-[#2D2622]">{l.phone ? `+${l.phone}` : "—"}</span>
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                  l.mode === "gallabox" && l.http_status >= 200 && l.http_status < 300 ? "bg-green-50 text-green-700" :
                  l.mode === "manual" ? "bg-[#F3ECDF] text-[#8B6F47]" :
                  l.mode === "noop" ? "bg-yellow-50 text-yellow-700" :
                  "bg-red-50 text-red-700"}`}>
                  {l.mode}{l.http_status ? ` · ${l.http_status}` : ""}
                </span>
                {l.mode === "manual" && l.wa_url && (
                  <a href={l.wa_url} target="_blank" rel="noreferrer"
                    className="text-[#2D2622] underline text-[11px]">Open in WhatsApp</a>
                )}
                <span className="text-[#8B6F47] text-[11px]">{l.trigger}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-[#8B6F47]">
        Cron secret is stored server-side. If Gallabox credentials are blank, entries are logged with a manual <code>wa.me</code> link you can click to send.
      </p>
    </div>
  );
}

function Field({ label, value, onSave, testId, secret }) {
  const [v, setV] = useState(value || "");
  const [edit, setEdit] = useState(false);
  useEffect(() => { setV(value || ""); }, [value]);
  const isMasked = secret && typeof value === "string" && value.includes("•");
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#8B6F47] font-semibold">{label}</label>
      <div className="flex gap-2">
        <input value={edit ? v : (value || "")}
          onFocus={() => { if (isMasked) { setV(""); setEdit(true); } else { setEdit(true); }}}
          onChange={(e) => { setV(e.target.value); setEdit(true); }}
          onBlur={() => { if (edit && v !== value) onSave(v); setEdit(false); }}
          data-testid={testId}
          className="flex-1 h-9 px-3 border border-[#E5D8C4] text-xs font-mono2 focus:outline-none focus:border-[#2D2622]"
          placeholder={isMasked ? "Click to replace" : ""}
          type={secret && !edit ? "password" : "text"} />
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5D8C4] p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">{label}</div>
      <div className={`font-mono2 text-3xl font-bold mt-2 ${accent}`}>{value}</div>
    </div>
  );
}

/* ---------------- Sizes ---------------- */
function Sizes() {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const load = (cid) => cid && api.get("/sizes", { params:{ category_id:cid } }).then(r=>setItems(r.data));
  useEffect(() => { api.get("/categories").then(r => { setCats(r.data); if(r.data[0]){ setCatId(r.data[0].id); load(r.data[0].id);} }); }, []);
  useEffect(() => { if (catId) load(catId); }, [catId]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/sizes", { category_id: catId, label }); setLabel(""); toast.success("Added"); load(catId); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/sizes/${id}`); toast.success("Deleted"); load(catId); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {cats.map(c => (
          <button key={c.id} onClick={()=>setCatId(c.id)}
            className={`px-3 py-2 text-xs uppercase tracking-[0.2em] border ${catId===c.id?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
            {c.name}
          </button>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-3 items-center">
        <input required placeholder="e.g. 1220x610 mm" data-testid="size-label" value={label}
          onChange={(e)=>setLabel(e.target.value)}
          className="flex-1 h-10 px-3 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
        <button data-testid="size-add" className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
          <Plus size={14}/> Add size
        </button>
      </form>
      <div className="bg-white border border-[#E5D8C4] divide-y divide-[#F3ECDF]">
        {items.map(s => (
          <div key={s.id} className="p-3 flex justify-between items-center">
            <div className="font-mono2 text-sm text-[#2D2622]">{s.label}</div>
            <button onClick={()=>remove(s.id)} className="p-2 text-[#8B6F47] hover:text-red-600"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Colours ---------------- */
function Colours() {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#B08968");
  const load = (cid) => cid && api.get("/colors", { params:{ category_id:cid } }).then(r=>setItems(r.data));
  useEffect(() => { api.get("/categories").then(r => { setCats(r.data); if(r.data[0]){ setCatId(r.data[0].id); load(r.data[0].id);} }); }, []);
  useEffect(() => { if (catId) load(catId); }, [catId]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/colors", { category_id: catId, name, hex_code: hex }); setName(""); toast.success("Added"); load(catId); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/colors/${id}`); toast.success("Deleted"); load(catId); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {cats.map(c => (
          <button key={c.id} onClick={()=>setCatId(c.id)}
            className={`px-3 py-2 text-xs uppercase tracking-[0.2em] border ${catId===c.id?"bg-[#2D2622] text-white border-[#2D2622]":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
            {c.name}
          </button>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-3 items-center">
        <input type="color" value={hex} onChange={(e)=>setHex(e.target.value)} className="h-10 w-14 border border-[#E5D8C4]" />
        <input required placeholder="Colour name" data-testid="color-name" value={name}
          onChange={(e)=>setName(e.target.value)}
          className="flex-1 h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        <button data-testid="color-add" className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
          <Plus size={14}/> Add
        </button>
      </form>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
        {items.map(c => (
          <div key={c.id} className="p-3 bg-white border border-[#E5D8C4] flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 border border-[#E5D8C4]" style={{ background: c.hex_code || "#e7e5e4" }} />
              <div className="font-medium text-sm text-[#2D2622]">{c.name}</div>
            </div>
            <button onClick={()=>remove(c.id)} className="p-1.5 text-[#8B6F47] hover:text-red-600"><Trash2 size={13}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Generic Named List ---------------- */
function NamedList({ collection, label, hasNote }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const load = () => api.get(`/${collection}`).then(r => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [collection]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/${collection}`, { name, note }); setName(""); setNote(""); toast.success("Added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    try { await api.delete(`/${collection}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="flex flex-wrap gap-2 items-center">
        <input required placeholder={label} data-testid={`${collection}-name`} value={name}
          onChange={(e)=>setName(e.target.value)}
          className="flex-1 min-w-[180px] h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        {hasNote && (
          <input placeholder="Note (optional)" value={note} onChange={(e)=>setNote(e.target.value)}
            className="flex-1 min-w-[160px] h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        )}
        <button data-testid={`${collection}-add`}
          className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
          <Plus size={14}/> Add
        </button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-2">
        {items.map(x => (
          <div key={x.id} className="flex justify-between items-center p-3 bg-white border border-[#E5D8C4]">
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#2D2622] truncate">{x.name}</div>
              {x.note && <div className="text-[10px] text-[#8B6F47] font-mono2 mt-0.5 truncate">{x.note}</div>}
            </div>
            <button onClick={()=>remove(x.id)} className="p-1.5 text-[#8B6F47] hover:text-red-600"><Trash2 size={13}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Custom Fields (Admin: dynamic form field manager) ---------------- */
function CustomFields() {
  const SCOPES = [
    { v: "add_lot", label: "Stock-In (Add Lot)" },
    { v: "stock_out", label: "Stock-Out (Dispatch)" },
    { v: "purchase_order", label: "Purchase Order" },
  ];
  const TYPES = ["text", "number", "date", "dropdown", "checkbox"];
  const [scope, setScope] = useState("add_lot");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ key: "", label: "", field_type: "text", required: false, options: "" });

  const load = () => api.get("/custom-fields", { params: { form_scope: scope } })
    .then(r => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scope]);

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/custom-fields", {
        form_scope: scope, key: f.key, label: f.label, field_type: f.field_type,
        required: f.required,
        options: f.field_type === "dropdown"
          ? f.options.split(",").map(s => s.trim()).filter(Boolean) : [],
        order: (items[items.length - 1]?.order || 0) + 10,
      });
      toast.success("Field added");
      setF({ key: "", label: "", field_type: "text", required: false, options: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (fld, patch) => {
    try { await api.patch(`/custom-fields/${fld.id}`, patch); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const remove = async (fld) => {
    if (!window.confirm(`Delete field "${fld.label}"? Existing lots keep their values.`)) return;
    try { await api.delete(`/custom-fields/${fld.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-[#5D5148]">
        Add optional fields to the Stock-In form without touching code. Values save on each lot under <code>custom_fields</code>.
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em]">Form:</div>
        {SCOPES.map(s => (
          <button key={s.v} onClick={() => setScope(s.v)}
            className={`h-9 px-3 text-xs uppercase tracking-widest border ${scope===s.v?"bg-[#2D2622] text-white border-[#2D2622]":"border-[#E5D8C4] text-[#5D5148]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <form onSubmit={add} className="bg-white border border-[#E5D8C4] p-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2">
          <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-1">Label</div>
          <input required data-testid="cf-label" value={f.label} onChange={(e)=>setF({...f, label:e.target.value})}
            placeholder="e.g. Batch Colour" className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-1">Key</div>
          <input required data-testid="cf-key" value={f.key}
            onChange={(e)=>setF({...f, key:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"_")})}
            placeholder="batch_colour" className="w-full h-10 px-3 border border-[#E5D8C4] text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-1">Type</div>
          <select data-testid="cf-type" value={f.field_type} onChange={(e)=>setF({...f, field_type:e.target.value})}
            className="w-full h-10 px-3 border border-[#E5D8C4] text-sm bg-white">
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-1">Options (dropdown only)</div>
          <input value={f.options} onChange={(e)=>setF({...f, options:e.target.value})}
            disabled={f.field_type !== "dropdown"}
            placeholder="A, B, C" className="w-full h-10 px-3 border border-[#E5D8C4] text-sm disabled:bg-[#FAF7F2] focus:outline-none focus:border-[#2D2622]" />
        </div>
        <div className="flex items-center gap-4">
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" data-testid="cf-required" checked={f.required}
              onChange={(e)=>setF({...f, required:e.target.checked})} />
            Required
          </label>
          <button disabled={busy} data-testid="cf-add"
            className="h-10 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <Plus size={14}/> Add
          </button>
        </div>
      </form>

      <div className="bg-white border border-[#E5D8C4] divide-y divide-[#F3ECDF]" data-testid="cf-list">
        {items.length === 0 && <div className="p-4 text-sm text-[#8B6F47] italic">No custom fields yet — add one above.</div>}
        {items.map(fld => (
          <div key={fld.id} className="p-3 flex flex-wrap items-center gap-3 justify-between">
            <div className="min-w-0">
              <div className="font-display font-semibold text-[#2D2622]">{fld.label}
                {fld.required && <span className="ml-2 text-[10px] text-[#C2410C]">REQUIRED</span>}
                {fld.hidden && <span className="ml-2 text-[10px] text-[#8B6F47]">HIDDEN</span>}
              </div>
              <div className="text-[11px] font-mono2 text-[#8B6F47]">
                {fld.key} · {fld.field_type}{fld.options?.length ? ` · [${fld.options.join(", ")}]` : ""}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-[11px] text-[#5D5148] flex items-center gap-1">
                <input type="checkbox" checked={!!fld.hidden}
                  onChange={(e)=>toggle(fld, { hidden: e.target.checked })} />
                Hidden
              </label>
              <label className="text-[11px] text-[#5D5148] flex items-center gap-1">
                <input type="checkbox" checked={!!fld.required}
                  onChange={(e)=>toggle(fld, { required: e.target.checked })} />
                Required
              </label>
              <button onClick={()=>remove(fld)} className="p-1.5 text-[#8B6F47] hover:text-red-600"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Security (Admin: change own or others' passwords) ---------------- */
function Security() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);

  const [ownOpen, setOwnOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(null); // holds target user

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="text-sm text-[#5D5148]">
        Manage account passwords. Every change requires a one-time code sent to <b>{user?.email}</b>. Passwords are bcrypt-hashed; nothing is ever stored in plaintext.
      </div>

      {/* Own password card */}
      <div className="bg-white border border-[#E5D8C4] p-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Change my password</div>
          <div className="text-xs text-[#8B6F47] mt-1">Signed in as <b className="text-[#2D2622]">{user?.email}</b>.</div>
        </div>
        <button onClick={()=>setOwnOpen(true)} data-testid="security-open-own"
          className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em]">
          Change my password
        </button>
      </div>

      {/* Others list */}
      <div className="bg-white border border-[#E5D8C4]">
        <div className="p-4 border-b border-[#F3ECDF]">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Reset another user's password</div>
          <div className="text-xs text-[#8B6F47] mt-1">Admin override — no current-password required, but a one-time code is sent to your email to authorise.</div>
        </div>
        <div className="divide-y divide-[#F3ECDF]" data-testid="security-users">
          {users.map(u => (
            <div key={u.id} className="p-3 flex items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="font-display font-semibold text-[#2D2622] truncate">{u.full_name || u.email}</div>
                <div className="text-[11px] font-mono2 text-[#8B6F47]">{u.email} · {u.role}</div>
              </div>
              <button onClick={()=>setOtherOpen(u)}
                data-testid={`security-reset-${u.email}`}
                className="h-9 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622] hover:text-[#2D2622]">
                Change password
              </button>
            </div>
          ))}
        </div>
      </div>

      {ownOpen && <PasswordDialog mode="own" adminEmail={user?.email}
        onClose={()=>setOwnOpen(false)} />}
      {otherOpen && <PasswordDialog mode="other" target={otherOpen} adminEmail={user?.email}
        onClose={()=>setOtherOpen(null)} />}
    </div>
  );
}

/** Two-step dialog: (1) request OTP → (2) submit new password + OTP.
 *  Used for both "change my own password" and "admin resets another user's password". */
function PasswordDialog({ mode, target, adminEmail, onClose }) {
  const [step, setStep] = useState(1); // 1 = request OTP, 2 = submit password+OTP
  const [otpBusy, setOtpBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "", otp: "" });

  const requestOtp = async () => {
    setOtpBusy(true);
    try {
      const r = await api.post("/auth/request-password-otp");
      toast.success(r.data?.message || `OTP sent to ${adminEmail}`);
      setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send OTP"); }
    finally { setOtpBusy(false); }
  };

  const submit = async () => {
    if (pw.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.next !== pw.confirm) return toast.error("Passwords do not match");
    if (!/^\d{4,8}$/.test(pw.otp.trim())) return toast.error("Enter the OTP sent to your email");
    if (mode === "own" && !pw.current) return toast.error("Enter your current password");
    setSubmitBusy(true);
    try {
      if (mode === "own") {
        await api.post("/auth/change-password", {
          current_password: pw.current, new_password: pw.next, otp: pw.otp.trim(),
        });
        toast.success("Your password was updated");
      } else {
        await api.post(`/users/${target.id}/change-password`, {
          new_password: pw.next, otp: pw.otp.trim(),
        });
        toast.success(`Password reset for ${target.email}`);
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setSubmitBusy(false); }
  };

  const title = mode === "own"
    ? "Change my password"
    : `Reset password · ${target?.email}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" data-testid="pw-dialog">
      <div className="bg-white border border-[#E5D8C4] w-full max-w-md p-5 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">{title}</div>
            <div className="text-xs text-[#8B6F47] mt-1">
              {step === 1
                ? `A 6-digit code will be sent to ${adminEmail}. It expires in 10 minutes.`
                : `Enter the OTP delivered to ${adminEmail} and the new password.`}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[#8B6F47] hover:text-[#2D2622]" data-testid="pw-close">
            <X size={16}/>
          </button>
        </div>

        {step === 1 && (
          <div className="pt-1">
            <button onClick={requestOtp} disabled={otpBusy} data-testid="pw-request-otp"
              className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] w-full inline-flex items-center justify-center gap-2">
              {otpBusy ? <Loader2 className="animate-spin" size={13}/> : null}
              Send OTP to {adminEmail}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            {mode === "own" && (
              <PwField label="Current password" value={pw.current}
                onChange={(v)=>setPw(s=>({ ...s, current: v }))} testId="pw-current" />
            )}
            <PwField label="New password (min 8)" value={pw.next}
              onChange={(v)=>setPw(s=>({ ...s, next: v }))} testId="pw-next" />
            <PwField label="Confirm new password" value={pw.confirm}
              onChange={(v)=>setPw(s=>({ ...s, confirm: v }))} testId="pw-confirm" />
            <div>
              <label className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2 block">One-time code (sent to {adminEmail})</label>
              <input value={pw.otp} onChange={(e)=>setPw(s=>({ ...s, otp: e.target.value }))}
                inputMode="numeric" maxLength={6} data-testid="pw-otp"
                className="w-full h-10 px-3 border border-[#E5D8C4] text-sm font-mono2 tracking-[0.4em] text-center focus:outline-none focus:border-[#2D2622]" />
              <button onClick={requestOtp} disabled={otpBusy} type="button"
                data-testid="pw-resend-otp"
                className="text-[11px] text-[#8B6F47] hover:text-[#2D2622] underline mt-1">
                {otpBusy ? "Sending…" : "Resend OTP"}
              </button>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} type="button"
                className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest">Cancel</button>
              <button onClick={submit} disabled={submitBusy} data-testid="pw-submit"
                className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-40">
                {submitBusy ? <Loader2 className="animate-spin" size={13}/> : null}
                {mode === "own" ? "Update my password" : "Reset password"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2 block">{label}</label>
      <div className="relative">
        <input type={show ? "text" : "password"} value={value}
          onChange={(e)=>onChange(e.target.value)} required minLength={8}
          data-testid={testId} autoComplete="new-password"
          className="w-full h-10 px-3 pr-16 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] font-mono2" />
        <button type="button" onClick={()=>setShow(s=>!s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-[#8B6F47] hover:text-[#2D2622]">
          {show ? "Hide" : "Show"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Users ---------------- */
const DEVICE_TYPES = [
  { key: "desktop", label: "Computer / Desktop" },
  { key: "tablet",  label: "Tablet (iPad / Android Tablet)" },
  { key: "mobile",  label: "Mobile Phone" },
];

function Users() {
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [f, setF] = useState({ email:"", full_name:"", password:"", role:"operator",
    warehouse_ids: [], allowed_device_types: ["desktop","tablet","mobile"] });
  const [selected, setSelected] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user: me } = useAuth();
  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/users/permissions").then(r => setPerms(r.data.permissions));
    api.get("/warehouses").then(r => setWarehouses(r.data)).catch(() => {});
  }, []);
  const createUser = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/users", f); toast.success("User created"); setShowNew(false);
      setF({ email:"", full_name:"", password:"", role:"operator",
        warehouse_ids: [], allowed_device_types: ["desktop","tablet","mobile"] }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const savePerms = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const payload = {
        permissions: selected.permissions, role: selected.role,
        is_active: selected.is_active,
        warehouse_ids: selected.warehouse_ids || [],
        billing_company_ids: selected.billing_company_ids || [],
        allowed_device_types: selected.allowed_device_types || [],
        full_name: selected.full_name,
      };
      if (selected.password) payload.password = selected.password;
      await api.patch(`/users/${selected.id}`, payload);
      toast.success("Saved"); setSelected(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const delUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`); toast.success("Deleted"); load();
  };
  const togglePerm = (p) => setSelected(s => ({
    ...s, permissions: s.permissions.includes(p) ? s.permissions.filter(x=>x!==p) : [...s.permissions, p]
  }));
  const toggleNewWh = (name) => setF(s => ({
    ...s, warehouse_ids: (s.warehouse_ids || []).includes(name)
      ? s.warehouse_ids.filter(x => x !== name)
      : [...(s.warehouse_ids || []), name]
  }));
  const toggleEditWh = (name) => setSelected(s => ({
    ...s, warehouse_ids: (s.warehouse_ids || []).includes(name)
      ? s.warehouse_ids.filter(x => x !== name)
      : [...(s.warehouse_ids || []), name]
  }));
  const toggleNewDev = (k) => setF(s => ({
    ...s, allowed_device_types: (s.allowed_device_types || []).includes(k)
      ? s.allowed_device_types.filter(x => x !== k)
      : [...(s.allowed_device_types || []), k]
  }));
  const toggleEditDev = (k) => setSelected(s => ({
    ...s, allowed_device_types: (s.allowed_device_types || []).includes(k)
      ? s.allowed_device_types.filter(x => x !== k)
      : [...(s.allowed_device_types || []), k]
  }));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#5D5148]">Manage who can access what. Admins bypass permission checks.</div>
        <button onClick={()=>setShowNew(true)} data-testid="user-new-btn"
          className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
          <Plus size={14}/> New user
        </button>
      </div>
      <div className="bg-white border border-[#E5D8C4] divide-y divide-[#F3ECDF]">
        {users.map(u => (
          <div key={u.id} className="p-4 flex flex-wrap gap-3 items-center justify-between">
            <div className="min-w-0">
              <div className="font-display font-semibold text-[#2D2622]">{u.full_name}</div>
              <div className="text-xs text-[#8B6F47] font-mono2">{u.email}</div>
            </div>
            <div className="flex gap-2 items-center">
              <span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${u.role==="admin"?"bg-[#2D2622] text-white":"bg-[#F3ECDF] text-[#5D5148]"}`}>{u.role}</span>
              <span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${u.is_active?"bg-green-50 text-green-700":"bg-red-50 text-red-700"}`}>
                {u.is_active?"Active":"Disabled"}
              </span>
              <button onClick={()=>setSelected({ ...u })}
                className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#E5D8C4] hover:border-[#2D2622]">Edit</button>
              <button onClick={()=>setPwTarget(u)}
                data-testid={`user-change-pw-${u.email}`}
                className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#E5D8C4] hover:border-[#2D2622]">Change password</button>
              <button onClick={()=>delUser(u)} className="p-2 text-[#8B6F47] hover:text-red-600"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
      {pwTarget && (
        <PasswordDialog
          mode={pwTarget.id === me?.id ? "own" : "other"}
          target={pwTarget}
          adminEmail={me?.email}
          onClose={()=>setPwTarget(null)}
        />
      )}
      {showNew && (
        <Modal onClose={()=>setShowNew(false)} title="New user">
          <form onSubmit={createUser} className="space-y-3">
            <input required data-testid="new-user-email" placeholder="Email" type="email" value={f.email}
              onChange={(e)=>setF({...f, email:e.target.value})}
              className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
            <input required data-testid="new-user-name" placeholder="Full name" value={f.full_name}
              onChange={(e)=>setF({...f, full_name:e.target.value})}
              className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
            <input required data-testid="new-user-password" placeholder="Password" type="password" value={f.password}
              onChange={(e)=>setF({...f, password:e.target.value})}
              className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
            <select value={f.role} onChange={(e)=>setF({...f, role:e.target.value})}
              className="w-full h-10 px-3 border border-[#E5D8C4] text-sm bg-white">
              <option value="operator">Operator</option>
              <option value="user">User (custom permissions)</option>
              <option value="admin">Admin</option>
            </select>
            {f.role !== "admin" && (
              <div>
                <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2">Assigned Warehouses</div>
                {warehouses.length === 0 ? (
                  <div className="text-xs text-[#8B6F47] italic">No warehouses configured yet.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                    {warehouses.map(w => (
                      <label key={w.id} data-testid={`new-user-wh-${w.name}`}
                        className={`flex items-center gap-2 p-2 border cursor-pointer text-[11px] ${(f.warehouse_ids || []).includes(w.name)?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
                        <input type="checkbox" checked={(f.warehouse_ids || []).includes(w.name)}
                          onChange={()=>toggleNewWh(w.name)} />
                        <span className="truncate">{w.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-[#8B6F47] mt-1">Leave empty for full access; select one or more to restrict to those warehouses.</div>
              </div>
            )}
            <div>
              <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2">Allowed sign-in devices</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                {DEVICE_TYPES.map(d => (
                  <label key={d.key} data-testid={`new-user-device-${d.key}`}
                    className={`flex items-center gap-2 p-2 border cursor-pointer text-[11px] ${(f.allowed_device_types || []).includes(d.key)?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
                    <input type="checkbox" checked={(f.allowed_device_types || []).includes(d.key)}
                      onChange={()=>toggleNewDev(d.key)} />
                    <span>{d.label}</span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-[#8B6F47] mt-1">
                At least one required; user is blocked at login if they try any other device type.
              </div>
            </div>
            <button disabled={busy} className="w-full h-10 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em]">
              {busy? <Loader2 className="animate-spin inline" size={14}/> : "Create"}
            </button>
          </form>
        </Modal>
      )}
      {selected && (
        <Modal onClose={()=>setSelected(null)} title={`Edit ${selected.full_name}`}>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2">Role</div>
              <select value={selected.role} onChange={(e)=>setSelected({...selected, role:e.target.value})}
                className="w-full h-10 px-3 border border-[#E5D8C4] text-sm bg-white">
                <option value="operator">Operator</option>
                <option value="user">User (custom)</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={selected.is_active}
                onChange={(e)=>setSelected({...selected, is_active:e.target.checked})} />
              Account enabled
            </label>
            {selected.role !== "admin" && (
              <div>
                <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2">Assigned Warehouses</div>
                {warehouses.length === 0 ? (
                  <div className="text-xs text-[#8B6F47] italic">No warehouses configured yet.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                    {warehouses.map(w => (
                      <label key={w.id} data-testid={`edit-user-wh-${w.name}`}
                        className={`flex items-center gap-2 p-2 border cursor-pointer text-[11px] ${(selected.warehouse_ids || []).includes(w.name)?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
                        <input type="checkbox" checked={(selected.warehouse_ids || []).includes(w.name)}
                          onChange={()=>toggleEditWh(w.name)} />
                        <span className="truncate">{w.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-[#8B6F47] mt-1">Empty = access to all warehouses. Select one or more to restrict stock and PO visibility to those warehouses only.</div>
              </div>
            )}
            <div>
              <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2">Allowed sign-in devices</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                {DEVICE_TYPES.map(d => (
                  <label key={d.key} data-testid={`edit-user-device-${d.key}`}
                    className={`flex items-center gap-2 p-2 border cursor-pointer text-[11px] ${(selected.allowed_device_types || []).includes(d.key)?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
                    <input type="checkbox" checked={(selected.allowed_device_types || []).includes(d.key)}
                      onChange={()=>toggleEditDev(d.key)} />
                    <span>{d.label}</span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-[#8B6F47] mt-1">
                Leave all boxes checked for unrestricted login; deselect to block a specific class of devices.
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2">Permissions</div>
              <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                {perms.map(p => (
                  <label key={p} className={`flex items-center gap-2 p-2 border cursor-pointer text-[11px] ${selected.permissions.includes(p)?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
                    <input type="checkbox" checked={selected.permissions.includes(p)} onChange={()=>togglePerm(p)} />
                    <span className="font-mono2">{p}</span>
                  </label>
                ))}
              </div>
            </div>
            <button onClick={savePerms} disabled={busy}
              className="w-full h-10 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
              {busy? <Loader2 className="animate-spin" size={14}/> : <><Save size={14}/> Save</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-lg border border-[#E5D8C4] p-6" onClick={(e)=>e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div className="font-display font-bold text-xl text-[#2D2622]">{title}</div>
          <button onClick={onClose} className="p-1 text-[#8B6F47] hover:text-[#2D2622]"><X size={16}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------------- PO Layout (print/PDF customization) ---------------- */
function POLayout() {
  const DEFAULT = {
    company_name: "", company_address: "", company_gstin: "",
    company_phone: "", company_email: "", company_logo_url: "",
    header_text: "Veneer Slate · Flexible Stone Veneer",
    header_style: "bordered",
    accent_color: "#2D2622",
    paper_size: "A4",
    column_order: ["sr", "colour", "size", "quantity"],
    show_expected_delivery: true,
    show_notes_block: true,
    show_lot_allocation: true,
    show_notes_column: true,
    show_grand_total: true,
    show_unallocated_callout: true,
    unallocated_placement: "top",
    show_signature_block: false,
    signature_label: "Authorised signatory",
    terms_text: "",
    footer_text: "",
  };
  const [f, setF] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/po-layout-settings").then(r => setF(x => ({ ...x, ...r.data })))
      .catch(() => {});
  }, []);
  const set = (patch) => setF(x => ({ ...x, ...patch }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/po-layout-settings", f);
      toast.success("PO layout updated · takes effect on next print");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const reset = () => { if (window.confirm("Reset to defaults?")) setF(DEFAULT); };

  const COLS = [
    { key: "sr", label: "Sr. / Line #" },
    { key: "colour", label: "Colour" },
    { key: "size", label: "Size" },
    { key: "quantity", label: "Quantity" },
  ];
  const move = (idx, dir) => {
    const next = [...f.column_order];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    set({ column_order: next });
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="po-layout-panel">
      <div className="space-y-5">
        {/* Identity ---------------------------------------------------- */}
        <div className="bg-white border border-[#E5D8C4] p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Company identity</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LayoutTextInput label="Company name" value={f.company_name} onChange={(v)=>set({company_name:v})} testId="po-layout-cname" />
            <LayoutTextInput label="Logo URL" value={f.company_logo_url} onChange={(v)=>set({company_logo_url:v})} placeholder="https://..." testId="po-layout-logo" />
            <LayoutTextInput label="Phone" value={f.company_phone} onChange={(v)=>set({company_phone:v})} testId="po-layout-phone" />
            <LayoutTextInput label="Email" value={f.company_email} onChange={(v)=>set({company_email:v})} testId="po-layout-email" />
            <LayoutTextInput label="GSTIN / Tax ID" value={f.company_gstin} onChange={(v)=>set({company_gstin:v})} testId="po-layout-gstin" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Address (multi-line)</div>
            <textarea rows={3} value={f.company_address}
              onChange={(e) => set({ company_address: e.target.value })}
              data-testid="po-layout-address"
              className="w-full px-3 py-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] resize-y" />
          </div>
        </div>

        {/* Header style ------------------------------------------------ */}
        <div className="bg-white border border-[#E5D8C4] p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Header &amp; style</div>
          <LayoutTextInput label="Header sub-title" value={f.header_text} onChange={(v)=>set({header_text:v})} testId="po-layout-header" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Header style</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { k: "bordered", label: "Classic B&W (original)" },
                { k: "accent",   label: "Coloured accent bar" },
              ].map(o => (
                <button key={o.k} type="button" onClick={() => set({ header_style: o.k })}
                  data-testid={`po-layout-style-${o.k}`}
                  className={`h-11 border text-xs ${f.header_style===o.k?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4] bg-white"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {f.header_style === "accent" && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Accent colour</div>
              <div className="flex items-center gap-2">
                <input type="color" value={f.accent_color} data-testid="po-layout-accent"
                  onChange={(e) => set({ accent_color: e.target.value })}
                  className="h-11 w-14 border border-[#E5D8C4] cursor-pointer" />
                <input type="text" value={f.accent_color}
                  onChange={(e) => set({ accent_color: e.target.value })}
                  className="h-11 px-3 border border-[#E5D8C4] w-32 text-sm font-mono2 focus:outline-none focus:border-[#2D2622]" />
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Paper size</div>
            <div className="grid grid-cols-3 gap-2">
              {["A4","A5","Letter"].map(p => (
                <button key={p} type="button" onClick={() => set({ paper_size: p })}
                  data-testid={`po-layout-paper-${p}`}
                  className={`h-11 border text-xs ${f.paper_size===p?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4] bg-white"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Columns ----------------------------------------------------- */}
        <div className="bg-white border border-[#E5D8C4] p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Line-item column order</div>
          <div className="space-y-2">
            {f.column_order.map((k, i) => {
              const col = COLS.find(c => c.key === k) || { label: k };
              return (
                <div key={k} className="flex items-center gap-2 p-2 border border-[#E5D8C4] bg-[#FAF7F2]"
                     data-testid={`po-layout-col-${k}`}>
                  <span className="text-[10px] font-mono2 text-[#8B6F47] w-6">{i + 1}.</span>
                  <span className="flex-1 text-sm text-[#2D2622]">{col.label}</span>
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                    className="h-8 w-8 border border-[#E5D8C4] text-xs disabled:opacity-30 hover:border-[#2D2622]">↑</button>
                  <button onClick={() => move(i, 1)} disabled={i === f.column_order.length - 1}
                    className="h-8 w-8 border border-[#E5D8C4] text-xs disabled:opacity-30 hover:border-[#2D2622]">↓</button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section toggles -------------------------------------------- */}
        <div className="bg-white border border-[#E5D8C4] p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Sections</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <ToggleCheckbox label="Expected delivery panel" value={f.show_expected_delivery} onChange={(v)=>set({show_expected_delivery:v})} testId="po-layout-show-eta" />
            <ToggleCheckbox label="General notes block"      value={f.show_notes_block}        onChange={(v)=>set({show_notes_block:v})}        testId="po-layout-show-notes" />
            <ToggleCheckbox label="Per-item notes on print"  value={f.show_notes_column}       onChange={(v)=>set({show_notes_column:v})}       testId="po-layout-show-item-notes" />
            <ToggleCheckbox label="Lot allocation table"     value={f.show_lot_allocation}     onChange={(v)=>set({show_lot_allocation:v})}     testId="po-layout-show-lots" />
            <ToggleCheckbox label="Grand-total row"          value={f.show_grand_total}        onChange={(v)=>set({show_grand_total:v})}        testId="po-layout-show-total" />
            <ToggleCheckbox label='"NEED TO BE PRODUCED" callout' value={f.show_unallocated_callout} onChange={(v)=>set({show_unallocated_callout:v})} testId="po-layout-show-unalloc" />
          </div>
          {f.show_unallocated_callout && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Callout placement</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { k: "top",       label: "Top of PO" },
                  { k: "per_group", label: "Per category" },
                  { k: "both",      label: "Both" },
                ].map(o => (
                  <button key={o.k} type="button" onClick={() => set({ unallocated_placement: o.k })}
                    data-testid={`po-layout-unalloc-${o.k}`}
                    className={`h-10 border text-[11px] ${f.unallocated_placement===o.k?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4] bg-white"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Signatures / footer / terms -------------------------------- */}
        <div className="bg-white border border-[#E5D8C4] p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Signatures &amp; footer</div>
          <ToggleCheckbox label="Print signature block" value={f.show_signature_block} onChange={(v)=>set({show_signature_block:v})} testId="po-layout-show-sig" />
          {f.show_signature_block && (
            <LayoutTextInput label="Signature line label" value={f.signature_label} onChange={(v)=>set({signature_label:v})} testId="po-layout-sig-label" />
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Terms &amp; conditions (multi-line)</div>
            <textarea rows={3} value={f.terms_text} onChange={(e)=>set({terms_text:e.target.value})}
              data-testid="po-layout-terms"
              className="w-full px-3 py-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] resize-y" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">Footer label</div>
            <textarea rows={2} value={f.footer_text} onChange={(e)=>set({footer_text:e.target.value})}
              data-testid="po-layout-footer"
              placeholder="Thanks for your business · +91 98765 43210"
              className="w-full px-3 py-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] resize-y" />
          </div>
        </div>

        {/* Actions ---------------------------------------------------- */}
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={busy} data-testid="po-layout-save"
            className="flex-1 h-11 bg-[#2D2622] text-white text-xs uppercase tracking-[0.25em] inline-flex items-center justify-center gap-2 hover:bg-[#5D4A2E] disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14}/> Save layout</>}
          </button>
          <button onClick={reset} type="button" className="h-11 px-4 border border-[#E5D8C4] text-xs uppercase tracking-[0.25em] hover:border-[#2D2622]">
            Reset defaults
          </button>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-white border border-[#E5D8C4] p-5">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Live preview</div>
        <div className="text-black text-xs" style={{ fontFamily: "Arial, sans-serif" }}>
          <div className="text-center py-2 mb-3"
               style={ f.header_style === "accent"
                 ? { background: f.accent_color, color: "white", border: `2px solid ${f.accent_color}` }
                 : { border: "2px solid #000" } }>
            <div className="font-black text-lg tracking-wide">ORDER</div>
          </div>
          {(f.company_name || f.company_logo_url) && (
            <div className="mb-2 border border-black p-2 flex items-start gap-2 text-[10px]">
              {f.company_logo_url && <img src={f.company_logo_url} alt="logo" style={{ maxHeight: 40, maxWidth: 80, objectFit: "contain" }} />}
              <div>
                <b>{f.company_name || "Company name"}</b>
                <div className="whitespace-pre-line">{f.company_address}</div>
                {f.company_gstin && <div>GSTIN: {f.company_gstin}</div>}
              </div>
            </div>
          )}
          <div className="text-center mb-2">
            <div className="font-bold text-sm">Sample Buyer Pvt Ltd</div>
            <div className="text-[10px]">{f.header_text || "—"}</div>
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th colSpan={f.column_order.length}
                    className="border border-black py-1 text-center"
                    style={ f.header_style === "accent"
                      ? { background: f.accent_color, color: "white" }
                      : { background: "#F3ECDF", color: "#000" } }>
                  ORDER DETAILS — SAMPLE
                </th>
              </tr>
              <tr>
                {f.column_order.map(k => {
                  const col = COLS.find(c => c.key === k) || { label: k };
                  return <th key={k} className="border border-black p-1 text-left text-[10px]">{col.label}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {f.column_order.map(k => (
                  <td key={k} className="border border-black p-1 text-[10px]">
                    {k === "sr" ? "1" : k === "colour" ? "IVORY" : k === "size" ? "1220×610" : "50"}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {f.terms_text && (
            <div className="mt-2 border border-black p-1 text-[9px] whitespace-pre-line">
              <b>TERMS:</b> {f.terms_text}
            </div>
          )}
          {f.show_signature_block && (
            <div className="mt-4 flex justify-end text-[10px]">
              <div className="text-center">
                <div style={{ borderTop: "1px solid #000", width: 120 }} />
                <div>{f.signature_label}</div>
              </div>
            </div>
          )}
          {f.footer_text && (
            <div className="mt-3 pt-1 border-t border-black text-center italic text-[10px] whitespace-pre-line"
                 style={{ color: f.header_style === "accent" ? f.accent_color : "#000" }}>
              {f.footer_text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LayoutTextInput({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1">{label}</div>
      <input value={value || ""} placeholder={placeholder} data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
    </label>
  );
}
function ToggleCheckbox({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 p-3 border border-[#E5D8C4] cursor-pointer">
      <input type="checkbox" checked={!!value} data-testid={testId}
        onChange={(e) => onChange(e.target.checked)} />
      <span className="text-xs">{label}</span>
    </label>
  );
}

/* ---------------- Racks (with per-warehouse assignment) ---------------- */
function RacksAdmin() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [f, setF] = useState({ name: "", warehouse_place: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", warehouse_place: "", note: "" });
  const [filter, setFilter] = useState("");
  const [seedStatus, setSeedStatus] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);

  const load = () => {
    // Admin needs to see every rack across every warehouse in this view — pass
    // no filter so backend returns the full set even for scoped operators
    // (they view Settings only if permitted anyway).
    api.get("/racks").then(r => setItems(r.data));
    api.get("/warehouses").then(r => setWarehouses(r.data));
    api.get("/admin/seed-status").then(r => setSeedStatus(r.data)).catch(() => setSeedStatus(null));
  };
  useEffect(() => { load(); }, []);

  const lockDefaults = async () => {
    if (!window.confirm(
      "Permanently lock every seeded collection (racks, lot numbers, factories, warehouses, billing companies)?\n\n" +
      "This will:\n" +
      "• Hard-delete every already-deleted default row so it can never resurrect\n" +
      "• Plant a seed-lock marker so future redeploys never re-add defaults\n\n" +
      "Existing active racks are NOT touched. Safe to re-run."
    )) return;
    setLockBusy(true);
    try {
      const r = await api.post("/admin/lock-seed-defaults");
      const rk = r.data?.collections?.racks;
      toast.success(
        `Locked · ${rk?.hard_deleted || 0} orphan racks purged · marker set`
      );
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Lock failed"); }
    finally { setLockBusy(false); }
  };

  const create = async () => {
    if (!f.name.trim()) return toast.error("Rack name required");
    setBusy(true);
    try {
      await api.post("/racks", f);
      toast.success(`Rack ${f.name} added`);
      setF({ name: "", warehouse_place: "", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({ name: r.name, warehouse_place: r.warehouse_place || "", note: r.note || "" });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/racks/${editingId}`, editForm);
      toast.success("Saved"); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete rack ${r.name}?`)) return;
    await api.delete(`/racks/${r.id}`); toast.success("Deleted"); load();
  };

  const filtered = filter
    ? items.filter(r => (r.warehouse_place || "").toLowerCase() === filter.toLowerCase())
    : items;

  return (
    <div className="space-y-5" data-testid="racks-admin">
      <div className="bg-white border border-[#E5D8C4] p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Add rack</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value.toUpperCase() })}
            placeholder="Rack no · e.g. A1"
            data-testid="rack-new-name"
            className="h-11 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] uppercase font-mono2" />
          <select value={f.warehouse_place}
            onChange={(e) => setF({ ...f, warehouse_place: e.target.value })}
            data-testid="rack-new-warehouse"
            className="h-11 px-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]">
            <option value="">— Unassigned —</option>
            {warehouses.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
          </select>
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
            placeholder="Note (optional)"
            className="h-11 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
          <button onClick={create} disabled={busy} data-testid="rack-new-btn"
            className="h-11 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.25em] disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={14}/> : "+ Add rack"}
          </button>
        </div>
      </div>

      {/* Seed-lock kill-switch panel. Sits ABOVE the rack list because the
          user's #1 complaint is default racks resurrecting on redeploy. */}
      <div data-testid="seed-lock-panel" className="bg-[#FFF7E6] border border-[#E0B36A] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">
              ✂ Lock seed defaults
            </div>
            <p className="text-xs text-[#5D5148] mt-1 leading-relaxed">
              Press once to permanently stop the app from re-adding default racks
              (and other seeded lists) on every redeploy. Hard-purges any
              already-deleted rows and plants a permanent lock marker in the DB.
              Safe to press repeatedly.
            </p>
            {seedStatus && (
              <div className="mt-2 text-[10px] font-mono2 text-[#5D5148] flex flex-wrap gap-3">
                {["racks","lot_numbers","factories","warehouses","billing_companies"].map(k => (
                  <span key={k} data-testid={`seed-status-${k}`}>
                    <b>{k}</b>: {seedStatus[k]?.marker_planted ? "🔒 locked" : "⚠ unlocked"} · {seedStatus[k]?.active_rows ?? "?"} active
                    {seedStatus[k]?.soft_deleted_rows ? ` · ${seedStatus[k].soft_deleted_rows} orphaned` : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button onClick={lockDefaults} disabled={lockBusy}
            data-testid="rack-lock-defaults-btn"
            className="h-11 px-4 bg-[#C2410C] text-white text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 hover:bg-[#8B2C08] disabled:opacity-50">
            {lockBusy ? <Loader2 className="animate-spin" size={14}/> : "🔒 Lock defaults permanently"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5D8C4] p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">
            All racks ({filtered.length})
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            data-testid="rack-filter-warehouse"
            className="h-9 px-2 border border-[#E5D8C4] text-xs focus:outline-none focus:border-[#2D2622]">
            <option value="">Filter — All warehouses</option>
            {warehouses.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
          </select>
        </div>
        <div className="divide-y divide-[#F3ECDF]">
          {filtered.map(r => editingId === r.id ? (
            <div key={r.id} className="py-2 grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
              <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value.toUpperCase() })}
                className="h-9 px-2 border border-[#E5D8C4] text-sm uppercase font-mono2 focus:outline-none focus:border-[#2D2622]" />
              <select value={editForm.warehouse_place}
                onChange={(e) => setEditForm({ ...editForm, warehouse_place: e.target.value })}
                className="h-9 px-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]">
                <option value="">— Unassigned —</option>
                {warehouses.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
              </select>
              <input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                className="h-9 px-2 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
              <button onClick={saveEdit}
                className="h-9 px-3 bg-[#2D2622] text-white text-xs uppercase tracking-widest">Save</button>
              <button onClick={() => setEditingId(null)}
                className="h-9 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest">Cancel</button>
            </div>
          ) : (
            <div key={r.id} className="py-2 flex items-center justify-between gap-2 flex-wrap"
                 data-testid={`rack-row-${r.name}`}>
              <div className="flex-1 min-w-0">
                <div className="font-mono2 text-sm text-[#2D2622]">
                  {r.name}
                  <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#E5D8C4] text-[#8B6F47]">
                    {r.warehouse_place || "unassigned"}
                  </span>
                </div>
                {r.note && <div className="text-[11px] text-[#8B6F47] mt-1">{r.note}</div>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(r)}
                  data-testid={`rack-edit-${r.name}`}
                  className="h-8 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest hover:border-[#2D2622]">Edit</button>
                <button onClick={() => del(r)}
                  className="h-8 px-3 border border-[#E5D8C4] text-xs uppercase tracking-widest text-red-700 hover:border-red-700">Delete</button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-[#8B6F47] italic py-4">No racks match this filter.</div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ---------------- Photo Cleanup (legacy photo rename migration) ---------------- */
function PhotoCleanup() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm("Rename every legacy photo to the new {Batch}_{Stage}_{Index}_{Timestamp} format? This can take a few minutes on large libraries and cannot be reversed.")) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/rename-legacy-photos");
      setResult(r.data);
      toast.success(`Migration done · ${r.data.renamed} renamed, ${r.data.skipped} skipped`);
    } catch (e) { toast.error(e.response?.data?.detail || "Migration failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-[#E5D8C4] p-5 space-y-3" data-testid="photo-cleanup-panel">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Legacy photo rename</div>
      <p className="text-xs text-[#5D5148]">
        Photos uploaded before the new naming scheme still carry random UUID filenames.
        This one-shot migration renames every legacy file on the server to
        <code className="mx-1 px-1 bg-[#F3ECDF]">{"{Batch}_{Stage}_{Index}_{YYYYMMDD-HHMMSS}"}</code>
        and rewrites every lot / stone-block reference to the new path. Safe to run
        multiple times — already-migrated files are skipped automatically.
      </p>
      <button onClick={run} disabled={busy} data-testid="photo-cleanup-btn"
        className="h-11 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 hover:bg-[#5D4A2E] disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={14}/> : "Run rename migration"}
      </button>
      {result && (
        <div className="mt-3 border border-[#E5D8C4] p-3 text-xs font-mono2" data-testid="photo-cleanup-result">
          <div>Renamed: <b>{result.renamed}</b></div>
          <div>Skipped: <b>{result.skipped}</b></div>
          {result.errors?.length > 0 && (
            <div className="mt-2 text-red-700">
              First errors: {result.errors.slice(0, 5).map(e => e.error).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ---------------- Metadata Scrub (strip EXIF / GPS / device from all images) ---------------- */
function MetadataScrub() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm("Strip EXIF, GPS, date, camera model and every other metadata tag from ALL existing photos in the library? Files are overwritten in place — pixel data is preserved, only the hidden metadata is removed. Safe to re-run.")) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/strip-image-metadata");
      setResult(r.data);
      toast.success(`Scrub done · ${r.data.cleaned} cleaned, ${r.data.skipped} skipped`);
    } catch (e) { toast.error(e.response?.data?.detail || "Scrub failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-[#E5D8C4] p-5 space-y-3" data-testid="metadata-scrub-panel">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Photo metadata scrub</div>
      <p className="text-xs text-[#5D5148]">
        New uploads are automatically scrubbed. This one-time job walks every
        existing photo on the server and strips EXIF, GPS coordinates, capture
        date, camera model, phone name, ICC profiles and any private tags —
        keeping the visible image intact. Idempotent: already-scrubbed files
        are skipped.
      </p>
      <button onClick={run} disabled={busy} data-testid="metadata-scrub-btn"
        className="h-11 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 hover:bg-[#5D4A2E] disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={14}/> : "Scrub all photo metadata"}
      </button>
      {result && (
        <div className="mt-3 border border-[#E5D8C4] p-3 text-xs font-mono2" data-testid="metadata-scrub-result">
          <div>Cleaned: <b>{result.cleaned}</b></div>
          <div>Skipped: <b>{result.skipped}</b></div>
          {result.errors?.length > 0 && (
            <div className="mt-2 text-red-700">
              First errors: {result.errors.slice(0, 5).map(e => e.error).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


